import React, { useEffect, useReducer, useState } from "react";
import { withStyles, Switch, CircularProgress } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import PageTitle from "../../../components/PageTitle/PageTitle";
import { pageConfiguracoes } from "../../../utils/getPageTitle";
import HeaderConfiguracoesButtonNovo from "../Components/HeaderConfiguracoesButtonNovo";
import Scroll from "../../../components/InfiniteScroll/Scroll";
import Table from "../../../components/Table/Table";
import Notification from "../../../components/Notification";
import { Button } from "../../../components/ui/Buttons";
import Dialog from "../../../components/Dialog/Dialog";
import CloseIcon from "../../../components/Icon/Close";
import MultiToggleButtons from "../../../components/Button/MultiToggleButtons";
import { InputForm } from "../../../components/Modal/Input";
import { TextFieldSearch } from "../../../components/TextField";
import {
  findAllGrupoProcedimento,
  findByIdGrupoProcedimento,
  createGrupoProcedimento,
  updateGrupoProcedimento,
} from "../../../services/GrupoProcedimentoService";
import { findAllProcedimentos } from "../../../services/ProcedimentoService";

const notificationDefault = { open: false, message: "", type: "" };

const pageableDTODefault = {
  pageNumber: 0,
  pageSize: 30,
  sortDir: "ASC",
  sortField: "nome",
};

const grupoProcedimentoDefault = {
  id: null,
  nome: "",
  ativo: true,
  procedimentos: [],
};

const columns = ({ onClickSwitch }) => [
  {
    Header: "Nome",
    field: "nome",
    getValue: (row) => row.nome,
  },
  {
    Header: "Procedimentos",
    getValue: (row) =>
      row.procedimentos?.map((p) => p.nome).join(", ") || "-",
  },
  {
    Header: "Status",
    getValue: (row) => (
      <Switch
        id={`cfg-grp-swt-sta-${row.id}`}
        checked={row.ativo}
        color="primary"
        onClick={(e) => {
          e.stopPropagation();
          onClickSwitch(row);
        }}
      />
    ),
    isClick: true,
  },
];

const GrupoProcedimentos = ({ classes }) => {
  const [search, setSearch] = useState("");
  const [grupos, setGrupos] = useState([]);
  const [last, setLast] = useState(false);
  const [pageableDTO, setPageableDTO] = useState(pageableDTODefault);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(notificationDefault);
  const [openModal, toggleModal] = useReducer((s) => !s, false);
  const [grupoSelecionado, setGrupoSelecionado] = useState(grupoProcedimentoDefault);
  const [loadingSave, setLoadingSave] = useState(false);

  useEffect(() => {
    loadGrupos({ isClearable: true });
  }, []);

  const loadGrupos = async (options) => {
    try {
      setLoading(true);
      const response = await findAllGrupoProcedimento({
        search: options?.searchClean ? "" : search,
        ativo: null,
        pageableDTO: {
          ...pageableDTO,
          pageNumber: options?.isClearable ? 0 : pageableDTO.pageNumber,
        },
      });

      const { content, last } = response;
      setGrupos(options?.isClearable ? content : [...grupos, ...content]);
      setLast(last);
      setPageableDTO({
        ...pageableDTO,
        pageNumber: options?.isClearable ? 1 : pageableDTO.pageNumber + 1,
      });
    } catch {
      openNotification("Erro ao carregar grupos de procedimentos", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClickNovo = () => {
    setGrupoSelecionado(grupoProcedimentoDefault);
    toggleModal();
  };

  const handleClickRow = async (row) => {
    try {
      const grupo = await findByIdGrupoProcedimento(row.id);
      setGrupoSelecionado(grupo);
      toggleModal();
    } catch {
      openNotification("Erro ao buscar grupo de procedimento", "error");
    }
  };

  const handleSave = async () => {
    try {
      if (!grupoSelecionado.nome) {
        openNotification("Preencha o nome do grupo", "error");
        return;
      }
      setLoadingSave(true);

      const payload = {
        ...(grupoSelecionado.id && { id: grupoSelecionado.id }),
        nome: grupoSelecionado.nome,
        ativo: grupoSelecionado.ativo,
        procedimentos: grupoSelecionado.procedimentos?.map((p) => ({ id: p.id })) || [],
      };

      if (grupoSelecionado.id) {
        await updateGrupoProcedimento(payload);
      } else {
        await createGrupoProcedimento(payload);
      }

      openNotification("Grupo salvo com sucesso", "success");
      toggleModal();
      loadGrupos({ isClearable: true });
    } catch {
      openNotification("Erro ao salvar grupo de procedimento", "error");
    } finally {
      setLoadingSave(false);
    }
  };

  const handleClickSwitch = async (row) => {
    try {
      await updateGrupoProcedimento({
        id: row.id,
        nome: row.nome,
        ativo: !row.ativo,
        procedimentos: row.procedimentos?.map((p) => ({ id: p.id })) || [],
      });
      loadGrupos({ isClearable: true });
    } catch {
      openNotification("Erro ao alterar status", "error");
    }
  };

  const loadProcedimentosOptions = async (searchTerm, loadedOptions, { page }) => {
    const response = await findAllProcedimentos({
      nome: searchTerm || "",
      ativo: true,
      pageableDTO: {
        pageNumber: page,
        pageSize: 20,
        sortDir: "ASC",
        sortField: "nome",
      },
    });

    const { content, last } = response?.data?.data?.findAllProcedimentos || {};

    return {
      options: content || [],
      hasMore: !last,
      additional: { page: page + 1 },
    };
  };

  const openNotification = (message, type) => {
    setNotification({ open: true, message, type });
    setTimeout(() => setNotification(notificationDefault), 3000);
  };

  const handleSearchChange = (e) => setSearch(e.target.value);

  const handleKeypress = (e) => {
    if (e.key === "Enter" && search.length >= 3) {
      loadGrupos({ isClearable: true });
    }
  };

  return (
    <div className={classes.container}>
      <PageTitle title={pageConfiguracoes("Grupo procedimentos")} />
      <HeaderConfiguracoesButtonNovo
        idPrefix="cfg-grp"
        title="Grupo procedimentos"
        labelButton="Novo grupo"
        handleClickNovo={handleClickNovo}
        search={{
          handleChange: handleSearchChange,
          value: search,
          onKeypress: handleKeypress,
          handleClickClear: () => {
            setSearch("");
            loadGrupos({ isClearable: true, searchClean: true });
          },
          onClickButtonSearch: () => loadGrupos({ isClearable: true }),
        }}
      />
      <div className={classes.tableContainer}>
        <Scroll
          loadMore={() => loadGrupos()}
          hasMore={!last}
          pageStart={0}
          initialLoad={false}
        >
          <Table
            dados={grupos}
            columns={columns({ onClickSwitch: handleClickSwitch })}
            clickable
            getDadosRow={handleClickRow}
          />
        </Scroll>
        {loading && (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        )}
      </div>
      <div className={classes.contentButtons}>
        <Button id="cfg-grp-btn-nov" shape="circle" onClick={handleClickNovo}>
          <Add />
        </Button>
      </div>

      {openModal && (
        <Dialog
          open={openModal}
          onClose={toggleModal}
          classes={{ paper: classes.dialogPaper }}
          fullWidth
        >
          <div className={classes.modalHeader}>
            <div className={classes.titleModal}>
              {grupoSelecionado.id ? "Editar grupo" : "Novo grupo"}
            </div>
            <Button
              id="cfg-grp-btn-fec"
              shape="circle"
              bgColor="#FB7676"
              style={{ height: 32, width: 32 }}
              onClick={toggleModal}
            >
              <CloseIcon />
            </Button>
          </div>
          <div className={classes.containerModal}>
            <MultiToggleButtons
              id="cfg-grp-btn-tab"
              options={["Informações", "Auditoria"]}
              tabSelected={0}
              changeTabSelected={() => {}}
              classes={{ buttonsContainer: classes.toggleButtons }}
              disabled={true}
              posicaoDesabilitada={[1]}
            />
            <div className={classes.contentModal}>
              <div className={classes.formContainer}>
                <div className={classes.row}>
                  <div className={classes.fieldContainerFullWidth}>
                    <div className={classes.titleField}>Nome *</div>
                    <InputForm
                      id="cfg-grp-inp-nom"
                      value={grupoSelecionado.nome}
                      onChange={(e) =>
                        setGrupoSelecionado({ ...grupoSelecionado, nome: e.target.value })
                      }
                      classes={{ input: classes.inputRetangular }}
                    />
                  </div>
                  {grupoSelecionado.id && (
                    <div className={classes.fieldAtivoContainer}>
                      <div className={classes.titleField}>Ativo</div>
                      <Switch
                        id="cfg-grp-swt-ati"
                        checked={grupoSelecionado.ativo}
                        color="primary"
                        onClick={() =>
                          setGrupoSelecionado({ ...grupoSelecionado, ativo: !grupoSelecionado.ativo })
                        }
                      />
                    </div>
                  )}
                </div>
                <div className={classes.fieldContainer}>
                  <div className={classes.titleField}>Procedimentos</div>
                  <TextFieldSearch
                    id="cfg-grp-sel-pro"
                    placeholder="Selecione os procedimentos"
                    classNotched={classes.notchedOutline}
                    classInput={classes.inputContainer}
                    loadOptions={loadProcedimentosOptions}
                    getOptionLabel={(option) => option.nome}
                    getOptionValue={(option) => option.id}
                    value={null}
                    onChange={(e) => {
                      if (e && !grupoSelecionado.procedimentos.some((p) => p.id === e.id)) {
                        setGrupoSelecionado({
                          ...grupoSelecionado,
                          procedimentos: [...grupoSelecionado.procedimentos, e],
                        });
                      }
                    }}
                    withPaginate
                    debounceTimeout={300}
                    additional={{ page: 0 }}
                    menuPosition="fixed"
                  />
                  {grupoSelecionado.procedimentos?.length > 0 && (
                    <div className={classes.chipContainer}>
                      {grupoSelecionado.procedimentos.map((proc) => (
                        <div key={proc.id} className={classes.chip}>
                          <span>{proc.nome}</span>
                          <span
                            className={classes.chipRemove}
                            onClick={() =>
                              setGrupoSelecionado({
                                ...grupoSelecionado,
                                procedimentos: grupoSelecionado.procedimentos.filter(
                                  (p) => p.id !== proc.id
                                ),
                              })
                            }
                          >
                            ×
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className={classes.footer}>
                <Button
                  id="cfg-grp-btn-can"
                  kind="transparent"
                  onClick={toggleModal}
                  disabled={loadingSave}
                  fullWidth
                >
                  Cancelar
                </Button>
                <Button
                  id="cfg-grp-btn-sal"
                  onClick={handleSave}
                  disabled={loadingSave}
                  fullWidth
                >
                  Salvar
                  {loadingSave && (
                    <CircularProgress color="inherit" size={14} style={{ marginLeft: 10 }} />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Dialog>
      )}

      <Notification
        close={() => setNotification(notificationDefault)}
        reset={() => setNotification(notificationDefault)}
        isOpen={notification.open}
        variant={notification.type}
        message={notification.message}
      />
    </div>
  );
};

const styles = {
  container: {
    gridArea: "content",
    display: "grid",
    gridTemplateRows: "auto 1fr",
    overflow: "hidden",
    position: "relative",
    "& thead th": {
      textAlign: "left",
      top: 0,
      position: "sticky",
    },
  },
  tableContainer: {
    display: "flex",
    flexDirection: "column",
    overflow: "scroll",
    padding: "24px 24px 0 24px",
    backgroundColor: "#F5F5F5",
    gap: "16px",
    borderRadius: "16px",
    "&> div": {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      height: "100%",
      overflow: "scroll",
      borderRadius: "16px",
      border: "solid 1px rgba(0, 0, 0, 0.1)",
      backgroundColor: "#FFF",
    },
  },
  loadingContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
  },
  contentButtons: {
    display: "flex",
    gap: "8px",
    position: "absolute",
    bottom: 24,
    right: 37,
  },
  dialogPaper: {
    maxWidth: "590px",
    maxHeight: "500px",
    height: "100%",
    overflow: "hidden",
  },
  modalHeader: {
    minHeight: 64,
    padding: "0px 32px",
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
    justifyContent: "space-between",
  },
  titleModal: {
    fontWeight: 700,
    fontSize: "18px",
    color: "#26ACA9",
  },
  containerModal: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: "16px 0",
    gap: "8px",
  },
  contentModal: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  toggleButtons: {
    margin: "0 32px",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "16px 32px",
    flex: 1,
    overflow: "auto",
  },
  fieldContainer: {
    display: "flex",
    flexDirection: "column",
  },
  fieldContainerFullWidth: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  fieldAtivoContainer: {
    display: "flex",
    flexDirection: "column",
    width: "40px",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    gap: "8px",
    alignItems: "flex-start",
  },
  titleField: {
    color: "#868686",
    fontWeight: 400,
    fontSize: "12px",
    marginBottom: "4px",
  },
  inputRetangular: {
    borderRadius: "8px",
  },
  notchedOutline: {
    border: "0",
  },
  inputContainer: {
    width: "calc(100% - 20px)",
    background: "#F2F2F2",
    color: "#505050",
    borderRadius: "8px",
    minHeight: "23px",
    padding: "4px 8px",
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    marginTop: "8px",
    maxHeight: "120px",
    overflowY: "auto",
  },
  chip: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    background: "#E8F5F4",
    border: "1px solid #26ACA9",
    borderRadius: "16px",
    padding: "4px 10px",
    fontSize: "12px",
    color: "#505050",
  },
  chipRemove: {
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: 700,
    color: "#868686",
    lineHeight: 1,
    "&:hover": {
      color: "#FB7676",
    },
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 32px 0",
    borderTop: "1px solid rgba(0, 0, 0, 0.1)",
  },
};

export default withStyles(styles)(GrupoProcedimentos);
